import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ArrowLeft, PauseCircle, PlayCircle, RotateCcw, Trash2, Undo2, XCircle } from 'lucide-react';

import type { JiraEpicSync } from '@/domain/models/development.js';
import { StatusBadge } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { JiraEpicStatus } from '@/presentation/pages/developments/development-detail/components/jira-epic-status.js';
import { jiraRetryLabel } from '@/presentation/pages/developments/utils/jira-sync-pending.js';
import type { DevelopmentStatus } from '@/shared/utils/format-status.js';

const HEADER_COMPACT_TRANSITION_MS = 250;

const compactIdentitySx = (visible: boolean): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
    overflow: 'hidden',
    flex: visible ? 1 : 0,
    maxWidth: visible ? '100%' : 0,
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transition: (theme) => theme.transitions.create(['opacity', 'max-width'], { duration: HEADER_COMPACT_TRANSITION_MS })
});

const expandedIdentitySx = (visible: boolean): SxProps<Theme> => ({
    overflow: 'hidden',
    maxHeight: visible ? 160 : 0,
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transition: (theme) => theme.transitions.create(['opacity', 'max-height'], { duration: HEADER_COMPACT_TRANSITION_MS })
});

interface ContextLineProps {
    clientAbbreviation: string;
    projectAbbreviation: string;
    jiraEpicKey: string | null;
    jiraEpicUrl: string | null;
    jiraEpicStatusName: string | null;
    epicSync: JiraEpicSync;
    creatingLabel: string;
    retryLabel: string;
    noWrap?: boolean;
    testId?: string;
}

function ContextLine({
    clientAbbreviation,
    projectAbbreviation,
    jiraEpicKey,
    jiraEpicUrl,
    jiraEpicStatusName,
    epicSync,
    creatingLabel,
    retryLabel,
    noWrap = false,
    testId
}: ContextLineProps) {
    const context = `${clientAbbreviation} · ${projectAbbreviation}`;
    const showEpic = epicSync.status === 'PENDING' || epicSync.status === 'RETRYING' || (epicSync.status === 'LINKED' && jiraEpicKey !== null);
    return (
        <Typography variant="caption" color="text.secondary" noWrap={noWrap} data-testid={testId} sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
            <span style={{ whiteSpace: 'pre' }}>{showEpic ? `${context} · ` : context}</span>
            {showEpic && (
                <JiraEpicStatus
                    status={epicSync.status}
                    epicKey={jiraEpicKey}
                    epicUrl={jiraEpicUrl}
                    epicStatusName={jiraEpicStatusName}
                    creatingLabel={creatingLabel}
                    retryLabel={retryLabel}
                    variant="inline"
                />
            )}
        </Typography>
    );
}

interface Props {
    displayId: string;
    status: DevelopmentStatus;
    title: string;
    jiraEpicKey: string | null;
    jiraEpicUrl?: string | null;
    jiraEpicStatusName: string | null;
    epicSync?: JiraEpicSync;
    clientAbbreviation: string;
    projectAbbreviation: string;
    isFullHeightTab: boolean;
    showReturnToAnalysis: boolean;
    canDeleteDev: boolean;
    canCancelDev: boolean;
    canReactivateDev: boolean;
    canImpedeDev: boolean;
    canResumeDev: boolean;
    onBack: () => void;
    onReturnToAnalysis: () => void;
    onDelete: () => void;
    onCancel: () => void;
    onReactivate: () => void;
    onImpede: () => void;
    onResume: () => void;
}

export function DevelopmentDetailHeader({
    displayId,
    status,
    title,
    jiraEpicKey,
    jiraEpicUrl = null,
    jiraEpicStatusName,
    epicSync = { status: 'NONE', nextAttemptAt: null },
    clientAbbreviation,
    projectAbbreviation,
    isFullHeightTab,
    showReturnToAnalysis,
    canDeleteDev,
    canCancelDev,
    canReactivateDev,
    canImpedeDev,
    canResumeDev,
    onBack,
    onReturnToAnalysis,
    onDelete,
    onCancel,
    onReactivate,
    onImpede,
    onResume
}: Props) {
    const { translate } = useApp();
    const epicCreatingLabel = translate('devDetail.jiraEpicCreating');
    const epicRetryLabel = jiraRetryLabel(translate, epicSync.nextAttemptAt);
    const buttonSize = isFullHeightTab ? 'small' : 'medium';

    const actions = (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {showReturnToAnalysis && (
                <Button variant="outlined" color="error" size={buttonSize} startIcon={<Undo2 size={16} />} onClick={onReturnToAnalysis}>
                    {translate('estimate.returnToAnalysis')}
                </Button>
            )}
            {canDeleteDev && status === 'EF_EM_ELABORACAO' && (
                <Button variant="contained" color="error" size={buttonSize} startIcon={<Trash2 size={16} />} onClick={onDelete}>
                    {translate('devDetail.deleteBtn')}
                </Button>
            )}
            {canImpedeDev && status === 'EM_DESENVOLVIMENTO' && (
                <Button variant="outlined" color="warning" size={buttonSize} startIcon={<PauseCircle size={16} />} onClick={onImpede}>
                    {translate('devDetail.impedeBtn')}
                </Button>
            )}
            {canResumeDev && status === 'IMPEDIDO' && (
                <Button variant="contained" size={buttonSize} startIcon={<PlayCircle size={16} />} sx={{ color: 'white' }} onClick={onResume}>
                    {translate('devDetail.resumeBtn')}
                </Button>
            )}
            {canCancelDev && status !== 'CANCELADO' && (
                <Button variant="contained" color="error" size={buttonSize} startIcon={<XCircle size={16} />} onClick={onCancel}>
                    {translate('devDetail.cancelBtn')}
                </Button>
            )}
            {canReactivateDev && status === 'CANCELADO' && (
                <Button variant="contained" size={buttonSize} startIcon={<RotateCcw size={16} />} sx={{ color: 'white' }} onClick={onReactivate}>
                    {translate('devDetail.reactivateBtn')}
                </Button>
            )}
        </Stack>
    );

    return (
        <Stack
            data-testid="development-detail-header"
            data-compact={isFullHeightTab ? 'true' : 'false'}
            direction="row"
            justifyContent="space-between"
            alignItems={isFullHeightTab ? 'center' : 'flex-start'}
            spacing={1}
            sx={{
                mb: isFullHeightTab ? 0.5 : 2,
                minWidth: 0,
                transition: (theme) => theme.transitions.create('margin-bottom', { duration: HEADER_COMPACT_TRANSITION_MS })
            }}
        >
            <Box sx={{ minWidth: 0, flex: 1, minHeight: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Button variant="text" size={buttonSize} startIcon={<ArrowLeft size={16} />} onClick={onBack} sx={{ px: 0, minWidth: 0, flexShrink: 0 }}>
                        {translate('common.back')}
                    </Button>
                    <Box sx={compactIdentitySx(isFullHeightTab)} aria-hidden={!isFullHeightTab}>
                        <Typography
                            variant="overline"
                            sx={{ fontFamily: 'monospace', lineHeight: 1, flexShrink: 0 }}
                            data-testid={isFullHeightTab ? 'development-detail-header-id' : undefined}
                        >
                            {displayId}
                        </Typography>
                        <StatusBadge status={status} />
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={title} data-testid={isFullHeightTab ? 'development-detail-header-title' : undefined}>
                            {title}
                        </Typography>
                        <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
                            <ContextLine
                                clientAbbreviation={clientAbbreviation}
                                projectAbbreviation={projectAbbreviation}
                                jiraEpicKey={jiraEpicKey}
                                jiraEpicUrl={jiraEpicUrl}
                                jiraEpicStatusName={jiraEpicStatusName}
                                epicSync={epicSync}
                                creatingLabel={epicCreatingLabel}
                                retryLabel={epicRetryLabel}
                                noWrap
                                testId={isFullHeightTab ? 'development-detail-header-jira-epic' : undefined}
                            />
                        </Box>
                    </Box>
                </Stack>
                <Box sx={expandedIdentitySx(!isFullHeightTab)} aria-hidden={isFullHeightTab}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="overline" sx={{ fontFamily: 'monospace' }} data-testid={isFullHeightTab ? undefined : 'development-detail-header-id'}>
                            {displayId}
                        </Typography>
                        <StatusBadge status={status} />
                    </Stack>
                    <Typography variant="h5" fontWeight={700} data-testid={isFullHeightTab ? undefined : 'development-detail-header-title'}>
                        {title}
                    </Typography>
                    <ContextLine
                        clientAbbreviation={clientAbbreviation}
                        projectAbbreviation={projectAbbreviation}
                        jiraEpicKey={jiraEpicKey}
                        jiraEpicUrl={jiraEpicUrl}
                        jiraEpicStatusName={jiraEpicStatusName}
                        epicSync={epicSync}
                        creatingLabel={epicCreatingLabel}
                        retryLabel={epicRetryLabel}
                        testId={isFullHeightTab ? undefined : 'development-detail-header-jira-epic'}
                    />
                </Box>
            </Box>
            {actions}
        </Stack>
    );
}
